import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch } from '@/shared/utils/get-url-params';
import { handleAxiosError } from '@/shared/utils/general/axios.utils';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import {
  AlertSeverityEnum,
} from '@/shared/interfaces/erp/global/errors.interface';
import {
  Empresa,
  EmpresaFreeEndpointPagingRes,
  EmpresasPagingRes
} from '@/shared/interfaces/erp/administration/empresas.interface';
import {
  UseMutationsParams,
} from '@/shared/interfaces/erp/global/global.interface';

import { useUiStore } from '@/store/ui/ui.store';

const { get, post, put } = erpApi();

///* tanSctack query
export const usefetchEmpresas = (
  params?: GetEmpresasParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['adminEmpresas', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getEmpresas(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetEmpresa = (id: string) => {
  return useQuery({
    queryKey: ['adminEmpresas', id],
    queryFn: async () => {
      return getEmpresa(id);
    },
  });
};

export const useCreateEmpresa = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createEmpresa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEmpresas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Empresa creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateEmpresa = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateEmpresa,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminEmpresas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Empresa actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useFetchEmpresasFreeEndpoint = (
  params?: GetEmpresasParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['adminEmpresasFreeEndpoint', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getEmpresasFreeEndpoint(params);
    },
    enabled,
    retry: false,
  });
};

///* axios
export type GetEmpresasParams = Partial<Empresa> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateEmpresaParams = Omit<Empresa, 'id_empresa'>;
export type UpdateEmpresaParams = {
  id: string;
  data: CreateEmpresaParams;
};

export const getEmpresas = async (params?: GetEmpresasParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<EmpresasPagingRes>(
    `${urlBackend}api/v1/empresa/?${queryParams}`,
    true
  );
};

export const getEmpresa = async (id: string) => {
  try {
    return await get<Empresa>(`${urlBackend}api/v1/empresa/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createEmpresa = async (data: CreateEmpresaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Empresa>(`${urlBackend}api/v1/empresa/`, data, true);
};

export const updateEmpresa = async (params: UpdateEmpresaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Empresa>(
    `${urlBackend}api/v1/empresa/${params.id}/`,
    params.data,
    true
  );
};

export const getEmpresasFreeEndpoint = async (
  params?: GetEmpresasParams
): Promise<EmpresaFreeEndpointPagingRes> => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<EmpresaFreeEndpointPagingRes>(
    `${urlBackend}api/v1/get-empresas/?${queryParams}`,
    false
  ) as any;
};
